import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  ActionIcon,
  Menu,
  Tooltip,
  Paper,
  Divider,
} from '@mantine/core';
import {
  IconMail,
  IconPhone,
  IconBriefcase,
  IconBuilding,
  IconMapPin,
  IconDots,
  IconEdit,
  IconTrash,
  IconStar,
  IconStarFilled,
  IconCalendar,
  IconMessage,
  IconNotes,
  IconTag,
} from '@tabler/icons-react';
import { Card, Button } from '../../ui';
import { FadeIn, HoverLift } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ContactData {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  mobile?: string;
  avatar?: string;
  title?: string;
  company?: string;
  companyId?: string;
  department?: string;
  address?: string;
  city?: string;
  country?: string;
  tags?: string[];
  score?: number;
  starred?: boolean;
  status?: 'active' | 'inactive' | 'lead' | 'customer' | 'prospect';
  source?: string;
  lastContactedAt?: Date;
  createdAt?: Date;
  notes?: string;
  socialProfiles?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  metadata?: Record<string, unknown>;
}

export interface ContactCardProps {
  contact: ContactData;
  variant?: 'default' | 'compact' | 'detailed' | 'horizontal';
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onEmail?: () => void;
  onCall?: () => void;
  onSchedule?: () => void;
  onMessage?: () => void;
  onToggleStar?: () => void;
  onClick?: () => void;
  showActions?: boolean;
  showTags?: boolean;
  showScore?: boolean;
  className?: string;
}

export const ContactCard = forwardRef<HTMLDivElement, ContactCardProps>(
  (
    {
      contact,
      variant = 'default',
      onView,
      onEdit,
      onDelete,
      onEmail,
      onCall,
      onSchedule,
      onMessage,
      onToggleStar,
      onClick,
      showActions = true,
      showTags = true,
      showScore = false,
      className,
    },
    ref
  ) => {
    const fullName = `${contact.firstName} ${contact.lastName}`;
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getStatusColor = (status?: ContactData['status']) => {
      switch (status) {
        case 'customer':
          return 'green';
        case 'lead':
          return 'blue';
        case 'prospect':
          return 'yellow';
        case 'inactive':
          return 'gray';
        default:
          return 'blue';
      }
    };

    const formatDate = (date?: Date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const renderActionsMenu = () => (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {onEmail && contact.email && (
            <Menu.Item leftSection={<IconMail size={14} />} onClick={onEmail}>
              Send Email
            </Menu.Item>
          )}
          {onCall && (contact.phone || contact.mobile) && (
            <Menu.Item leftSection={<IconPhone size={14} />} onClick={onCall}>
              Call
            </Menu.Item>
          )}
          {onSchedule && (
            <Menu.Item leftSection={<IconCalendar size={14} />} onClick={onSchedule}>
              Schedule Meeting
            </Menu.Item>
          )}
          {onMessage && (
            <Menu.Item leftSection={<IconMessage size={14} />} onClick={onMessage}>
              Send Message
            </Menu.Item>
          )}
          <Menu.Divider />
          {onEdit && (
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
              Edit
            </Menu.Item>
          )}
          {onDelete && (
            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
              Delete
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    );

    if (variant === 'compact') {
      return (
        <FadeIn>
          <HoverLift lift={2}>
            <Paper
              ref={ref}
              className={className}
              p="sm"
              withBorder
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              onClick={onClick}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <Avatar src={contact.avatar} size="sm" radius="xl">
                    {fullName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Group gap="xs">
                      <Text size="sm" fw={500} style={accessibilityStyles}>{fullName}</Text>
                      {contact.starred && (
                        <IconStarFilled size={12} color="var(--mantine-color-yellow-5)" />
                      )}
                    </Group>
                    {contact.company && (
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>{contact.company}</Text>
                    )}
                  </Box>
                </Group>
                <Group gap="xs">
                  {contact.status && (
                    <Badge size="xs" color={getStatusColor(contact.status)}>
                      {contact.status}
                    </Badge>
                  )}
                  {showActions && renderActionsMenu()}
                </Group>
              </Group>
            </Paper>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'horizontal') {
      return (
        <FadeIn>
          <HoverLift lift={4}>
            <Card
              ref={ref}
              className={className}
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              onClick={onClick}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="md" wrap="nowrap">
                  <Avatar src={contact.avatar} size="lg" radius="md">
                    {fullName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Group gap="xs">
                      <Text fw={600} style={accessibilityStyles}>{fullName}</Text>
                      {contact.starred && (
                        <IconStarFilled size={14} color="var(--mantine-color-yellow-5)" />
                      )}
                      {contact.status && (
                        <Badge size="xs" color={getStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                      )}
                    </Group>
                    <Group gap="xs">
                      {contact.title && <Text size="sm" c="dimmed" style={accessibilityStyles}>{contact.title}</Text>}
                      {contact.company && (
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>at {contact.company}</Text>
                      )}
                    </Group>
                  </Box>
                </Group>
                <Group gap="lg" wrap="nowrap">
                  {contact.email && (
                    <Tooltip label={contact.email}>
                      <ActionIcon
                        variant="subtle"
                        component="a"
                        href={`mailto:${contact.email}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconMail size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {contact.phone && (
                    <Tooltip label={contact.phone}>
                      <ActionIcon
                        variant="subtle"
                        component="a"
                        href={`tel:${contact.phone}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconPhone size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {showScore && contact.score !== undefined && (
                    <Badge variant="light" color="blue">
                      Score: {contact.score}
                    </Badge>
                  )}
                  {showActions && renderActionsMenu()}
                </Group>
              </Group>
            </Card>
          </HoverLift>
        </FadeIn>
      );
    }

    if (variant === 'detailed') {
      return (
        <FadeIn>
          <Card ref={ref} className={className}>
            <Stack gap="md">
              {/* Header */}
              <Group justify="space-between">
                <Group gap="md">
                  <Avatar src={contact.avatar} size={80} radius="md">
                    {fullName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Group gap="xs">
                      <Text size="xl" fw={600} style={accessibilityStyles}>{fullName}</Text>
                      {onToggleStar && (
                        <ActionIcon
                          variant="subtle"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleStar();
                          }}
                        >
                          {contact.starred ? (
                            <IconStarFilled size={18} color="var(--mantine-color-yellow-5)" />
                          ) : (
                            <IconStar size={18} />
                          )}
                        </ActionIcon>
                      )}
                    </Group>
                    {contact.title && (
                      <Group gap="xs">
                        <IconBriefcase size={14} color="gray" />
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{contact.title}</Text>
                      </Group>
                    )}
                    {contact.company && (
                      <Group gap="xs">
                        <IconBuilding size={14} color="gray" />
                        <Text size="sm" c="dimmed" style={accessibilityStyles}>{contact.company}</Text>
                      </Group>
                    )}
                  </Box>
                </Group>
                <Group gap="xs">
                  {contact.status && (
                    <Badge size="lg" color={getStatusColor(contact.status)}>
                      {contact.status}
                    </Badge>
                  )}
                  {showActions && renderActionsMenu()}
                </Group>
              </Group>

              <Divider />

              {/* Contact info */}
              <Stack gap="xs">
                {contact.email && (
                  <Group gap="xs">
                    <IconMail size={16} color="gray" />
                    <Text size="sm" component="a" href={`mailto:${contact.email}`} style={accessibilityStyles}>
                      {contact.email}
                    </Text>
                  </Group>
                )}
                {contact.phone && (
                  <Group gap="xs">
                    <IconPhone size={16} color="gray" />
                    <Text size="sm" component="a" href={`tel:${contact.phone}`} style={accessibilityStyles}>
                      {contact.phone}
                    </Text>
                  </Group>
                )}
                {(contact.city || contact.country) && (
                  <Group gap="xs">
                    <IconMapPin size={16} color="gray" />
                    <Text size="sm" style={accessibilityStyles}>
                      {[contact.city, contact.country].filter(Boolean).join(', ')}
                    </Text>
                  </Group>
                )}
              </Stack>

              {/* Tags */}
              {showTags && contact.tags && contact.tags.length > 0 && (
                <Group gap="xs">
                  <IconTag size={14} color="gray" />
                  {contact.tags.map((tag) => (
                    <Badge key={tag} size="sm" variant="light">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              )}

              {/* Notes */}
              {contact.notes && (
                <Paper p="sm" bg="gray.0" radius="sm">
                  <Group gap="xs" mb="xs">
                    <IconNotes size={14} />
                    <Text size="xs" fw={500} style={accessibilityStyles}>Notes</Text>
                  </Group>
                  <Text size="sm" style={accessibilityStyles}>{contact.notes}</Text>
                </Paper>
              )}

              {/* Metadata */}
              <Group gap="lg">
                {contact.source && (
                  <Box>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Source</Text>
                    <Text size="sm" style={accessibilityStyles}>{contact.source}</Text>
                  </Box>
                )}
                {contact.lastContactedAt && (
                  <Box>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Last Contacted</Text>
                    <Text size="sm" style={accessibilityStyles}>{formatDate(contact.lastContactedAt)}</Text>
                  </Box>
                )}
                {showScore && contact.score !== undefined && (
                  <Box>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Lead Score</Text>
                    <Text size="sm" fw={600} style={accessibilityStyles}>{contact.score}</Text>
                  </Box>
                )}
              </Group>

              {/* Quick actions */}
              <Group gap="xs">
                {onEmail && contact.email && (
                  <Button variant="subtle" leftSection={<IconMail size={16} />} onClick={onEmail}>
                    Email
                  </Button>
                )}
                {onCall && contact.phone && (
                  <Button variant="subtle" leftSection={<IconPhone size={16} />} onClick={onCall}>
                    Call
                  </Button>
                )}
                {onSchedule && (
                  <Button variant="subtle" leftSection={<IconCalendar size={16} />} onClick={onSchedule}>
                    Schedule
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
        </FadeIn>
      );
    }

    // Default variant
    return (
      <FadeIn>
        <HoverLift lift={4}>
          <Card
            ref={ref}
            className={className}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
          >
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Avatar src={contact.avatar} size="lg" radius="xl">
                    {fullName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Group gap="xs">
                      <Text fw={600} style={accessibilityStyles}>{fullName}</Text>
                      {contact.starred && (
                        <IconStarFilled size={14} color="var(--mantine-color-yellow-5)" />
                      )}
                    </Group>
                    {contact.title && <Text size="sm" c="dimmed" style={accessibilityStyles}>{contact.title}</Text>}
                    {contact.company && (
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>{contact.company}</Text>
                    )}
                  </Box>
                </Group>
                {showActions && renderActionsMenu()}
              </Group>

              {contact.status && (
                <Badge color={getStatusColor(contact.status)} variant="light">
                  {contact.status}
                </Badge>
              )}

              {showTags && contact.tags && contact.tags.length > 0 && (
                <Group gap="xs">
                  {contact.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} size="xs" variant="outline">
                      {tag}
                    </Badge>
                  ))}
                  {contact.tags.length > 3 && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>+{contact.tags.length - 3}</Text>
                  )}
                </Group>
              )}

              <Group gap="xs">
                {contact.email && (
                  <Tooltip label="Email">
                    <ActionIcon
                      variant="subtle"
                      component="a"
                      href={`mailto:${contact.email}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconMail size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {contact.phone && (
                  <Tooltip label="Call">
                    <ActionIcon
                      variant="subtle"
                      component="a"
                      href={`tel:${contact.phone}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconPhone size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Stack>
          </Card>
        </HoverLift>
      </FadeIn>
    );
  }
);

ContactCard.displayName = 'ContactCard';

export default ContactCard;
